import { supabase } from "../supabase/client";
import { demoService } from "../demo/demoService";
import { isAbortError } from "../utils/abort";
import { getTransactionCategoryIconKey } from "../../lib/transactionCategoryIcons";

export interface TransactionCategory {
  transaction_id: string;
  user_id?: string | null;
  group_id?: string | null;
  general_category?: string | null;
  category_icon?: string | null;
  federal_category?: string | null;
  oregon_category?: string | null;
  custom_description?: string | null;
  ai_processed: boolean;
  ai_confidence?: "high" | "medium" | "low" | "user_verified" | "uncertain";
  ai_reasoning?: string | null;
  needs_user_input: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface LearnedPattern {
  id?: string;
  user_id?: string;
  group_id?: string;
  merchant_pattern?: string;
  transaction_pattern?: string;
  general_category: string;
  federal_category?: string;
  oregon_category?: string;
  confidence_score: number;
  times_used: number;
  times_confirmed: number;
  times_rejected: number;
  last_used?: string;
  created_at?: string;
}

export interface LearnedPatternInput {
  merchant_pattern?: string;
  transaction_pattern?: string;
  general_category: string;
  federal_category?: string | null;
  oregon_category?: string | null;
  confidence_score?: number;
  times_used?: number;
  times_confirmed?: number;
  times_rejected?: number;
}

class TransactionCategoriesDB {
  // Get user's primary group ID
  private async getUserGroupId(): Promise<string | null> {
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) return null;

      // Query group_members table (not user_groups which doesn't exist)
      // Get the first active group membership for this user
      const { data, error } = await supabase
        .from("group_members")
        .select("group_id")
        .eq("user_id", user.id)
        .eq("status", "active")
        .limit(1)
        .single();

      if (error || !data) {
        console.error(
          "[DEBUG] Error fetching user group from group_members:",
          error
        );
        return null;
      }

      console.log("[DEBUG] Found user group_id:", data.group_id);
      return data.group_id;
    } catch (error) {
      console.error("[DEBUG] Exception getting user group:", error);
      return null;
    }
  }

  // Get all categories for current user
  async getAllCategories(): Promise<TransactionCategory[]> {
    try {
      if (demoService.isDemo()) {
        return [];
      }
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) {
        return [];
      }
      const { data, error } = await supabase
        .from("transaction_categories")
        .select("*")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false });

      if (error) {
        console.error("Error fetching categories:", error);
        return [];
      }

      return data || [];
    } catch (error) {
      console.error("Database connection error:", error);
      return [];
    }
  }

  // Get categories for specific transactions
  async getCategoriesForTransactions(
    transactionIds: string[]
  ): Promise<TransactionCategory[]> {
    if (demoService.isDemo()) {
      return [];
    }
    let session = null;
    try {
      // Check session before querying
      const {
        data: { session: activeSession },
      } = await supabase.auth.getSession();
      session = activeSession;
      console.log("[DEBUG] getCategoriesForTransactions START", {
        requestedCount: transactionIds.length,
        sampleIds: transactionIds.slice(0, 3),
        hasSession: !!session,
        sessionUser: session?.user?.email,
        sessionExpiry: session?.expires_at,
      });
    } catch (error) {
      if (isAbortError(error)) {
        console.debug(
          "[DEBUG] getCategoriesForTransactions session aborted",
          error
        );
        return [];
      }
      console.error(
        "[DEBUG] getCategoriesForTransactions session failed",
        error
      );
    }

    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) {
        return [];
      }
      if (transactionIds.length === 0) {
        return [];
      }

      // Batch queries in chunks of 100 to avoid Supabase limits
      const BATCH_SIZE = 100;
      const CONCURRENCY = 5;
      const batches: string[][] = [];
      for (let i = 0; i < transactionIds.length; i += BATCH_SIZE) {
        batches.push(transactionIds.slice(i, i + BATCH_SIZE));
      }
      const allCategories: TransactionCategory[] = [];
      const errors: Array<{ index: number; message: string }> = [];
      let batchIndex = 0;
      console.log("[DEBUG] Fetching category batches", {
        batches: batches.length,
        batchSize: BATCH_SIZE,
        concurrency: CONCURRENCY,
      });

      const worker = async () => {
        while (true) {
          const currentIndex = batchIndex;
          batchIndex += 1;
          if (currentIndex >= batches.length) return;
          const batch = batches[currentIndex];
          console.log(
            `[DEBUG] Fetching batch ${currentIndex + 1}/${batches.length}`
          );

          const { data, error } = await supabase
            .from("transaction_categories")
            .select("*")
            .eq("user_id", user.id)
            .in("transaction_id", batch);

          if (error) {
            errors.push({ index: currentIndex, message: error.message });
            console.error("[DEBUG] Error fetching categories batch:", {
              message: error.message,
              fullError: error,
            });
            continue;
          }

          if (data) {
            allCategories.push(...data);
          }
        }
      };

      const workers = Array.from(
        { length: Math.min(CONCURRENCY, batches.length) },
        () => worker()
      );
      await Promise.all(workers);

      if (errors.length > 0) {
        console.warn("[DEBUG] Category batch errors", {
          errors: errors.length,
          sample: errors[0],
        });
      }

      console.log("[DEBUG] getCategoriesForTransactions SUCCESS", {
        foundCount: allCategories.length,
        sample: allCategories[0],
      });

      return allCategories;
    } catch (error) {
      console.error(
        "[DEBUG] Exception in getCategoriesForTransactions:",
        error
      );
      return [];
    }
  }

  // Get transactions that need user input
  async getTransactionsNeedingInput(): Promise<TransactionCategory[]> {
    if (demoService.isDemo()) {
      return [];
    }
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) {
      return [];
    }
    const { data, error } = await supabase
      .from("transaction_categories")
      .select("*")
      .eq("user_id", user.id)
      .eq("needs_user_input", true)
      .order("created_at", { ascending: true });

    if (error) {
      console.error("Error fetching transactions needing input:", error);
      return [];
    }

    return data || [];
  }

  // Upsert categories (insert or update)
  async upsertCategories(categories: TransactionCategory[]): Promise<boolean> {
    console.log("[DEBUG] upsertCategories START", {
      count: categories.length,
      sample: categories[0],
      supabaseUrl: process.env.REACT_APP_SUPABASE_URL?.substring(0, 30),
      hasSupabaseClient: !!supabase.from,
      isRealSupabase: !!(supabase as any).auth,
    });

    try {
      if (demoService.isDemo()) {
        return true;
      }
      // Get user's group_id
      const groupId = await this.getUserGroupId();
      console.log("[DEBUG] User group_id:", groupId);

      if (!groupId) {
        console.error("[DEBUG] No group_id found for user");
        return false;
      }

      // Get current user
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) {
        console.error("[DEBUG] No authenticated user");
        return false;
      }

      // Add group_id and user_id to all categories
      const categoriesWithGroup = categories.map((cat) => ({
        ...cat,
        category_icon:
          cat.category_icon ??
          (cat.general_category
            ? getTransactionCategoryIconKey(cat.general_category)
            : null),
        group_id: groupId,
        user_id: user.id,
      }));

      console.log("[DEBUG] Categories with group_id:", {
        sample: categoriesWithGroup[0],
      });

      const { data, error } = await supabase
        .from("transaction_categories")
        .upsert(categoriesWithGroup, {
          onConflict: "transaction_id",
          ignoreDuplicates: false,
        })
        .select(); // Add select to see what was actually saved

      if (error) {
        console.error("[DEBUG] Supabase error details:", {
          message: error.message,
          details: (error as any).details,
          hint: (error as any).hint,
          code: (error as any).code,
          fullError: error,
        });
        return false;
      }

      console.log("[DEBUG] upsertCategories SUCCESS", {
        savedCount: data?.length || 0,
        firstRow: data?.[0],
        allIds: data?.map((d: any) => d.transaction_id),
      });
      return true;
    } catch (error) {
      console.error("[DEBUG] Exception in upsertCategories:", error);
      return false;
    }
  }

  // Update single category
  async updateCategory(
    transactionId: string,
    updates: Partial<TransactionCategory>
  ): Promise<boolean> {
    if (demoService.isDemo()) {
      return true;
    }
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) {
      return false;
    }
    const { error } = await supabase
      .from("transaction_categories")
      .update({
        ...updates,
        category_icon:
          updates.category_icon ??
          (updates.general_category
            ? getTransactionCategoryIconKey(updates.general_category)
            : updates.general_category === null
              ? null
              : undefined),
      })
      .eq("transaction_id", transactionId)
      .eq("user_id", user.id);

    if (error) {
      console.error("Error updating category:", error);
      return false;
    }

    return true;
  }

  // Clear categories for specific transactions (current user only)
  async clearCategoriesForTransactions(
    transactionIds: string[]
  ): Promise<boolean> {
    try {
      if (demoService.isDemo()) {
        return true;
      }
      if (transactionIds.length === 0) {
        return true;
      }
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) {
        return false;
      }
      console.log("[DEBUG] Clearing categories for transactions", {
        count: transactionIds.length,
      });
      const updates = {
        general_category: null,
        category_icon: null,
        federal_category: null,
        oregon_category: null,
        ai_processed: false,
        ai_confidence: null,
        ai_reasoning: null,
        needs_user_input: true,
      } as any;
      const { error } = await supabase
        .from("transaction_categories")
        .update(updates)
        .eq("user_id", user.id)
        .in("transaction_id", transactionIds);

      if (error) {
        console.error("[DEBUG] Error clearing categories:", error);
        return false;
      }

      return true;
    } catch (error) {
      console.error("[DEBUG] Exception clearing categories:", error);
      return false;
    }
  }

  // Clear all categories for current user
  async clearAllCategories(): Promise<boolean> {
    try {
      if (demoService.isDemo()) {
        return true;
      }
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) {
        return false;
      }
      const { error } = await supabase
        .from("transaction_categories")
        .delete()
        .eq("user_id", user.id);

      if (error) {
        console.error("Error clearing categories:", error);
        return false;
      }

      return true;
    } catch (error) {
      console.error("Database connection error:", error);
      return false;
    }
  }

  // Get learned patterns for current user
  async getLearnedPatterns(): Promise<LearnedPattern[]> {
    try {
      if (demoService.isDemo()) {
        return [];
      }
      // Check session before querying
      try {
        const {
          data: { session },
        } = await supabase.auth.getSession();
        console.log("[DEBUG] getLearnedPatterns - Session check", {
          hasSession: !!session,
          sessionUser: session?.user?.email,
          sessionExpiry: session?.expires_at,
        });
      } catch (error) {
        if (isAbortError(error)) {
          console.debug("[DEBUG] getLearnedPatterns session aborted", error);
          return [];
        }
        console.error("[DEBUG] getLearnedPatterns session failed", error);
      }

      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) {
        return [];
      }
      const { data, error } = await supabase
        .from("learned_patterns")
        .select("*")
        .eq("user_id", user.id)
        .order("confidence_score", { ascending: false })
        .order("times_used", { ascending: false });

      if (error) {
        console.error("Error fetching learned patterns:", error);
        return [];
      }

      return data || [];
    } catch (error) {
      console.error("Database connection error:", error);
      return [];
    }
  }

  // Find matching patterns for a transaction
  async findMatchingPatterns(
    merchantName?: string,
    transactionName?: string
  ): Promise<LearnedPattern[]> {
    try {
      if (demoService.isDemo()) {
        return [];
      }
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) {
        return [];
      }
      let query = supabase
        .from("learned_patterns")
        .select("*")
        .eq("user_id", user.id);

      if (merchantName) {
        query = query.ilike("merchant_pattern", `%${merchantName}%`);
      }

      if (transactionName) {
        query = query.ilike("transaction_pattern", `%${transactionName}%`);
      }

      const { data, error } = await query
        .order("confidence_score", { ascending: false })
        .limit(5);

      if (error) {
        console.error("Error finding matching patterns:", error);
        return [];
      }

      return data || [];
    } catch (error) {
      console.error("Database connection error:", error);
      return [];
    }
  }

  // Save a new learned pattern
  async saveLearnedPattern(pattern: LearnedPattern): Promise<boolean> {
    try {
      if (demoService.isDemo()) {
        return true;
      }
      // Get user's group_id
      const groupId = await this.getUserGroupId();
      if (!groupId) {
        console.error("[DEBUG] No group_id found for user");
        return false;
      }

      // Get current user
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) {
        console.error("[DEBUG] No authenticated user");
        return false;
      }

      // Check if similar pattern exists
      const existing = await this.findMatchingPatterns(
        pattern.merchant_pattern,
        pattern.transaction_pattern
      );

      if (existing.length > 0) {
        // Update existing pattern
        const match = existing[0];
        const { error } = await supabase
          .from("learned_patterns")
          .update({
            times_used: match.times_used + 1,
            times_confirmed: match.times_confirmed + 1,
            confidence_score: Math.min(1.0, match.confidence_score + 0.1),
            last_used: new Date().toISOString(),
          })
          .eq("id", match.id);

        if (error) {
          console.error("Error updating learned pattern:", error);
          return false;
        }
      } else {
        // Insert new pattern with group_id and user_id
        const { error } = await supabase.from("learned_patterns").insert([
          {
            ...pattern,
            group_id: groupId,
            user_id: user.id,
          },
        ]);

        if (error) {
          console.error("Error saving learned pattern:", error);
          return false;
        }
      }

      return true;
    } catch (error) {
      console.error("Database connection error:", error);
      return false;
    }
  }

  async createLearnedPattern(
    pattern: LearnedPatternInput
  ): Promise<LearnedPattern | null> {
    try {
      if (demoService.isDemo()) {
        return null;
      }
      const groupId = await this.getUserGroupId();
      if (!groupId) {
        console.error("[DEBUG] No group_id found for user");
        return null;
      }
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) {
        console.error("[DEBUG] No authenticated user");
        return null;
      }
      const payload = {
        user_id: user.id,
        group_id: groupId,
        merchant_pattern: pattern.merchant_pattern?.trim() || null,
        transaction_pattern: pattern.transaction_pattern?.trim() || null,
        general_category: pattern.general_category.trim(),
        federal_category: pattern.federal_category?.trim() || null,
        oregon_category: pattern.oregon_category?.trim() || null,
        confidence_score: Math.max(
          0,
          Math.min(1, pattern.confidence_score ?? 1)
        ),
        times_used: pattern.times_used ?? 1,
        times_confirmed: pattern.times_confirmed ?? 1,
        times_rejected: pattern.times_rejected ?? 0,
        last_used: new Date().toISOString(),
      };
      const { data, error } = await supabase
        .from("learned_patterns")
        .insert([payload])
        .select("*")
        .single();
      if (error) {
        console.error("Error creating learned pattern:", error);
        return null;
      }
      return data;
    } catch (error) {
      console.error("Database connection error:", error);
      return null;
    }
  }

  async updateLearnedPattern(
    patternId: string,
    updates: LearnedPatternInput
  ): Promise<LearnedPattern | null> {
    try {
      if (demoService.isDemo()) {
        return null;
      }
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) {
        return null;
      }
      const payload: any = {
        merchant_pattern: updates.merchant_pattern?.trim() || null,
        transaction_pattern: updates.transaction_pattern?.trim() || null,
        general_category: updates.general_category.trim(),
        federal_category: updates.federal_category?.trim() || null,
        oregon_category: updates.oregon_category?.trim() || null,
        confidence_score: Math.max(
          0,
          Math.min(1, updates.confidence_score ?? 1)
        ),
      };
      if (typeof updates.times_used === "number") {
        payload.times_used = updates.times_used;
      }
      if (typeof updates.times_confirmed === "number") {
        payload.times_confirmed = updates.times_confirmed;
      }
      if (typeof updates.times_rejected === "number") {
        payload.times_rejected = updates.times_rejected;
      }

      const { data, error } = await supabase
        .from("learned_patterns")
        .update(payload)
        .eq("id", patternId)
        .eq("user_id", user.id)
        .select("*")
        .single();
      if (error) {
        console.error("Error updating learned pattern:", error);
        return null;
      }
      return data;
    } catch (error) {
      console.error("Database connection error:", error);
      return null;
    }
  }

  async deleteLearnedPattern(patternId: string): Promise<boolean> {
    try {
      if (demoService.isDemo()) {
        return true;
      }
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) {
        return false;
      }
      const { error } = await supabase
        .from("learned_patterns")
        .delete()
        .eq("id", patternId)
        .eq("user_id", user.id);
      if (error) {
        console.error("Error deleting learned pattern:", error);
        return false;
      }
      return true;
    } catch (error) {
      console.error("Database connection error:", error);
      return false;
    }
  }

  // Update pattern confidence (when user confirms or rejects)
  async updatePatternConfidence(
    patternId: string,
    confirmed: boolean
  ): Promise<boolean> {
    if (demoService.isDemo()) {
      return true;
    }
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) {
      return false;
    }
    const { data: pattern, error: fetchError } = await supabase
      .from("learned_patterns")
      .select("*")
      .eq("id", patternId)
      .eq("user_id", user.id)
      .single();

    if (fetchError || !pattern) {
      console.error("Error fetching pattern:", fetchError);
      return false;
    }

    const updates = confirmed
      ? {
          times_confirmed: pattern.times_confirmed + 1,
          confidence_score: Math.min(1.0, pattern.confidence_score + 0.05),
        }
      : {
          times_rejected: pattern.times_rejected + 1,
          confidence_score: Math.max(0.0, pattern.confidence_score - 0.1),
        };

    const { error } = await supabase
      .from("learned_patterns")
      .update(updates)
      .eq("id", patternId)
      .eq("user_id", user.id);

    if (error) {
      console.error("Error updating pattern confidence:", error);
      return false;
    }

    return true;
  }

  // Migrate from localStorage to Supabase (one-time migration)
  async migrateFromLocalStorage(): Promise<number> {
    if (demoService.isDemo()) {
      return 0;
    }
    const stored = localStorage.getItem("transactionTaxCategories");
    if (!stored) return 0;

    try {
      const categories = JSON.parse(stored);
      const toUpsert: TransactionCategory[] = [];

      for (const [transactionId, data] of Object.entries(categories)) {
        const category = data as any;
        toUpsert.push({
          transaction_id: transactionId,
          general_category: category.general || null,
          category_icon: getTransactionCategoryIconKey(category.general || ""),
          federal_category: category.federal || null,
          oregon_category: category.oregon || null,
          ai_processed: category.aiProcessed || false,
          ai_confidence: category.aiConfidence || null,
          ai_reasoning: category.aiReasoning || null,
          needs_user_input: category.aiFailed || false,
        });
      }

      if (toUpsert.length > 0) {
        const success = await this.upsertCategories(toUpsert);
        if (success) {
          // Clear localStorage after successful migration
          localStorage.removeItem("transactionTaxCategories");
          return toUpsert.length;
        }
      }
    } catch (error) {
      console.error("Error migrating from localStorage:", error);
    }

    return 0;
  }
}

export const transactionCategoriesDB = new TransactionCategoriesDB();
